import React from "react";
import styled from "styled-components";

import Button from "../button/Button";
import type { DmPendingAmendment } from "../../app/services/dmThreads";
import { describeAmendmentChanges } from "../../utils/contractAmendment";

interface ContractAmendmentPanelProps {
  amendment: DmPendingAmendment;
  /** 承認する側（受託者）か、提案した側（委託者）か。 */
  role: "contractor" | "requester";
  /** 受託者が「内容を確認」を押したとき。承認 / 却下はモーダル側で行う。 */
  onReview?: () => void;
  onWithdraw?: () => void;
  /** 送信中はボタンを無効化する（二重送信防止）。 */
  isBusy?: boolean;
  /** BE から返ったエラーメッセージ（残高不足・減額不可など）。 */
  errorMessage?: string | null;
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Heading = styled.div.attrs({ className: "bodytext-11" })`
  color: var(--soremo-blue);
`;

const Summary = styled.div.attrs({ className: "bodytext-13" })``;

const ErrorNote = styled.div.attrs({ className: "bodytext-13" })`
  color: var(--soremo-green);
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
`;

/**
 * 契約カード内に出す「承認待ちの契約更新」の案内。
 *
 * 差分そのものはタイムラインの `contract_amendment_proposed` メッセージと、
 * 受託者が開く `ContractAmendmentReviewModal` で見せる。ここは初回オファーの
 * 「詳細を確認」と同じく、**確認への導線**に徹する。
 */
const ContractAmendmentPanel: React.FC<ContractAmendmentPanelProps> = ({
  amendment,
  role,
  onReview,
  onWithdraw,
  isBusy = false,
  errorMessage,
}) => {
  const rows = describeAmendmentChanges(amendment.changes);
  if (rows.length === 0) return null;

  const fieldNames = rows.map((row) => row.label).join("・");

  return (
    <Panel data-testid="contract-amendment-panel" data-role={role}>
      <Heading>契約更新の承認待ち</Heading>
      <Summary>
        {role === "contractor"
          ? `${fieldNames}の変更が提案されています。`
          : `${fieldNames}の変更を提案しました。受託者の承認を待っています。`}
      </Summary>
      {errorMessage && <ErrorNote>{errorMessage}</ErrorNote>}
      <Actions>
        {role === "contractor" ? (
          <Button
            size="small"
            theme="primary"
            width="fit"
            rightIcon="chevron_right"
            disabled={isBusy}
            onClick={onReview}
          >
            内容を確認
          </Button>
        ) : (
          <Button
            size="small"
            theme="tertiary"
            variant="outlined"
            width="fit"
            disabled={isBusy}
            onClick={onWithdraw}
          >
            取り下げ
          </Button>
        )}
      </Actions>
    </Panel>
  );
};

export default ContractAmendmentPanel;
